import React, { useState, useMemo, useEffect, useCallback } from 'react';
import TopBar from '../components/layout/TopBar';
import { Icon } from '@iconify/react';
import { GlassCard, Button, Badge, InputField, Modal, SectionHeader } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';

// ─── Types ──────────────────────────────────────────────────

type RuleType = 'PERCENTAGE' | 'FIXED' | 'TIERED' | 'TEAM_SPLIT' | 'OVERRIDE';
type RuleStatus = 'ACTIVE' | 'DRAFT' | 'ARCHIVED';
type ViewMode = 'list' | 'builder' | 'templates' | 'analytics' | 'settings';

interface CommissionRule {
  id: string;
  name: string;
  description: string;
  type: RuleType;
  status: RuleStatus;
  priority: number;
  conditions: RuleCondition[];
  actions: RuleAction[];
  effectiveFrom?: string;
  effectiveTo?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  appliedCount: number;
  totalEarnings: number;
}

interface RuleCondition {
  id: string;
  field: string;
  operator: string;
  value: any;
}

interface RuleAction {
  id: string;
  type: 'PERCENTAGE' | 'FIXED' | 'MULTIPLIER';
  value: number;
  target: string;
}

interface RuleTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  config: Partial<CommissionRule>;
}

// ─── Mock Data ──────────────────────────────────────────────

const MOCK_RULES: CommissionRule[] = [
  {
    id: 'r1',
    name: 'Standard Agent Commission',
    description: 'Base 5% commission for all agents on completed sales',
    type: 'PERCENTAGE',
    status: 'ACTIVE',
    priority: 1,
    conditions: [
      { id: 'c1', field: 'role', operator: 'equals', value: 'AGENT' },
      { id: 'c2', field: 'saleStatus', operator: 'equals', value: 'COMPLETED' },
    ],
    actions: [
      { id: 'a1', type: 'PERCENTAGE', value: 5, target: 'saleAmount' },
    ],
    effectiveFrom: '2024-01-01',
    createdBy: 'Admin',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
    appliedCount: 127,
    totalEarnings: 635000,
  },
  {
    id: 'r2',
    name: 'Manager Bonus - High Value Sales',
    description: 'Additional 2% for sales above ₱500,000',
    type: 'TIERED',
    status: 'ACTIVE',
    priority: 2,
    conditions: [
      { id: 'c3', field: 'role', operator: 'equals', value: 'MANAGER' },
      { id: 'c4', field: 'saleAmount', operator: 'greaterThan', value: 500000 },
    ],
    actions: [
      { id: 'a2', type: 'PERCENTAGE', value: 2, target: 'saleAmount' },
    ],
    effectiveFrom: '2024-01-01',
    createdBy: 'Admin',
    createdAt: '2024-01-10T00:00:00Z',
    updatedAt: '2024-01-10T00:00:00Z',
    appliedCount: 23,
    totalEarnings: 276000,
  },
  {
    id: 'r3',
    name: 'Team Split - Referral Bonus',
    description: '1% split for referring agent on team sales',
    type: 'TEAM_SPLIT',
    status: 'ACTIVE',
    priority: 3,
    conditions: [
      { id: 'c5', field: 'hasReferrer', operator: 'equals', value: true },
    ],
    actions: [
      { id: 'a3', type: 'PERCENTAGE', value: 1, target: 'saleAmount' },
    ],
    effectiveFrom: '2024-02-01',
    createdBy: 'Manager Cruz',
    createdAt: '2024-01-20T00:00:00Z',
    updatedAt: '2024-01-20T00:00:00Z',
    appliedCount: 8,
    totalEarnings: 32000,
  },
  {
    id: 'r4',
    name: 'Q1 Promotion - Double Commission',
    description: 'Limited time 10% commission for Q1 2024',
    type: 'OVERRIDE',
    status: 'DRAFT',
    priority: 0,
    conditions: [
      { id: 'c6', field: 'saleDate', operator: 'between', value: ['2024-01-01', '2024-03-31'] },
    ],
    actions: [
      { id: 'a4', type: 'PERCENTAGE', value: 10, target: 'saleAmount' },
    ],
    effectiveFrom: '2024-01-01',
    effectiveTo: '2024-03-31',
    createdBy: 'Admin',
    createdAt: '2024-01-25T00:00:00Z',
    updatedAt: '2024-01-25T00:00:00Z',
    appliedCount: 0,
    totalEarnings: 0,
  },
];

const RULE_TEMPLATES: RuleTemplate[] = [
  {
    id: 't1',
    name: 'Percentage Commission',
    description: 'Simple percentage-based commission on sale amount',
    category: 'Basic',
    icon: 'solar:percent-bold-duotone',
    color: '#0D9488',
    config: {
      type: 'PERCENTAGE',
      conditions: [{ id: 'c1', field: 'saleStatus', operator: 'equals', value: 'COMPLETED' }],
      actions: [{ id: 'a1', type: 'PERCENTAGE', value: 5, target: 'saleAmount' }],
    },
  },
  {
    id: 't2',
    name: 'Tiered Commission',
    description: 'Different rates based on sale value tiers',
    category: 'Advanced',
    icon: 'solar:layers-bold-duotone',
    color: '#3B82F6',
    config: {
      type: 'TIERED',
      conditions: [],
      actions: [],
    },
  },
  {
    id: 't3',
    name: 'Team Split',
    description: 'Distribute commission among team members',
    category: 'Team',
    icon: 'solar:users-group-rounded-bold-duotone',
    color: '#8B5CF6',
    config: {
      type: 'TEAM_SPLIT',
      conditions: [],
      actions: [],
    },
  },
  {
    id: 't4',
    name: 'Fixed Amount',
    description: 'Fixed commission amount per sale',
    category: 'Basic',
    icon: 'solar:wallet-money-bold-duotone',
    color: '#F59E0B',
    config: {
      type: 'FIXED',
      conditions: [],
      actions: [{ id: 'a1', type: 'FIXED', value: 5000, target: 'sale' }],
    },
  },
  {
    id: 't5',
    name: 'Role-Based',
    description: 'Different rates for different roles',
    category: 'Advanced',
    icon: 'solar:shield-user-bold-duotone',
    color: '#EC4899',
    config: {
      type: 'PERCENTAGE',
      conditions: [{ id: 'c1', field: 'role', operator: 'equals', value: 'AGENT' }],
      actions: [],
    },
  },
  {
    id: 't6',
    name: 'Product-Based',
    description: 'Commission based on product type or category',
    category: 'Advanced',
    icon: 'solar:box-bold-duotone',
    color: '#06B6D4',
    config: {
      type: 'PERCENTAGE',
      conditions: [{ id: 'c1', field: 'productCategory', operator: 'equals', value: 'PREMIUM' }],
      actions: [],
    },
  },
];

const RULE_TYPE_CONFIG: Record<RuleType, { label: string; icon: string; color: string }> = {
  PERCENTAGE: { label: 'Percentage', icon: 'solar:percent-bold-duotone', color: '#0D9488' },
  FIXED: { label: 'Fixed Amount', icon: 'solar:wallet-money-bold-duotone', color: '#F59E0B' },
  TIERED: { label: 'Tiered', icon: 'solar:layers-bold-duotone', color: '#3B82F6' },
  TEAM_SPLIT: { label: 'Team Split', icon: 'solar:users-group-rounded-bold-duotone', color: '#8B5CF6' },
  OVERRIDE: { label: 'Override', icon: 'solar:shield-warning-bold-duotone', color: '#EF4444' },
};

const STATUS_CONFIG: Record<RuleStatus, { label: string; variant: any; icon: string }> = {
  ACTIVE: { label: 'Active', variant: 'available', icon: 'solar:check-circle-bold-duotone' },
  DRAFT: { label: 'Draft', variant: 'info', icon: 'solar:document-bold-duotone' },
  ARCHIVED: { label: 'Archived', variant: 'default', icon: 'solar:archive-bold-duotone' },
};

const formatCurrency = (n: number) => `₱${n.toLocaleString()}`;
const formatDate = (d: string) => new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });

// ─── Component ──────────────────────────────────────────────

const CommissionRules: React.FC = () => {
  const { addToast } = useToast();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [rules, setRules] = useState<CommissionRule[]>(MOCK_RULES);

  const fetchRules = useCallback(async () => {
    try {
      const data = await api.commissionRules.list();
      if (data && Array.isArray(data) && data.length > 0) {
        const mapped: CommissionRule[] = data.map((r: any) => ({
          id: r.id,
          name: r.name,
          description: r.rule_json?.description || '',
          type: (r.rule_json?.type as RuleType) || 'PERCENTAGE',
          status: r.is_active ? 'ACTIVE' as RuleStatus : 'DRAFT' as RuleStatus,
          priority: r.rule_json?.priority || 1,
          conditions: r.rule_json?.conditions?.all?.map((c: any, i: number) => ({ id: `c${i}`, field: c.fact, operator: c.operator, value: c.value })) || [],
          actions: r.rule_json?.event ? [{ id: 'a1', type: 'PERCENTAGE' as const, value: r.rule_json.event.params?.rate || 0, target: 'saleAmount' }] : [],
          createdBy: 'Admin',
          createdAt: r.createdAt || new Date().toISOString(),
          updatedAt: r.updatedAt || new Date().toISOString(),
          appliedCount: 0,
          totalEarnings: 0,
        }));
        setRules(prev => {
          const apiIds = new Set(mapped.map(r => r.id));
          const mockOnly = prev.filter(r => !apiIds.has(r.id));
          return [...mapped, ...mockOnly];
        });
      }
    } catch {
      // keep mock data as fallback
    }
  }, []);

  useEffect(() => { fetchRules(); }, [fetchRules]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<RuleStatus | 'ALL'>('ALL');
  const [typeFilter, setTypeFilter] = useState<RuleType | 'ALL'>('ALL');
  const [selectedRule, setSelectedRule] = useState<CommissionRule | null>(null);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<CommissionRule | null>(null);

  // Builder state
  const [builderRule, setBuilderRule] = useState<CommissionRule>({
    id: '', name: '', description: '', type: 'PERCENTAGE', status: 'DRAFT',
    priority: 1, conditions: [], actions: [], createdBy: 'Current User',
    createdAt: '', updatedAt: '', appliedCount: 0, totalEarnings: 0,
  });
  const [showSimulator, setShowSimulator] = useState(false);
  const [simSaleAmount, setSimSaleAmount] = useState('150000');
  const [simRole, setSimRole] = useState('AGENT');

  // Settings state
  const [settings, setSettings] = useState({
    defaultRate: 5,
    minRate: 1,
    maxRate: 15,
    autoApproveBelow: 50000,
    requireManagerApproval: true,
    requireBrokerApproval: false,
    payoutSchedule: 'MONTHLY',
    taxDeduction: 0,
    enableOverrides: true,
    enableTeamSplit: true,
    enableTiered: true,
    notifyOnEarning: true,
    notifyOnPayout: true,
    notifyOnRuleChange: true,
    currency: 'PHP',
    roundingMode: 'NEAREST',
  });

  // ─── Filtered Rules ───────────────────────────────────────
  const filteredRules = useMemo(() => {
    return rules.filter(rule => {
      const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           rule.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'ALL' || rule.status === statusFilter;
      const matchesType = typeFilter === 'ALL' || rule.type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [rules, searchQuery, statusFilter, typeFilter]);

  // ─── Handlers ─────────────────────────────────────────────
  const handleToggleStatus = (rule: CommissionRule) => {
    const newStatus = rule.status === 'ACTIVE' ? 'DRAFT' : 'ACTIVE';
    setRules(prev => prev.map(r => r.id === rule.id ? { ...r, status: newStatus } : r));
    addToast('success', 'Rule Updated', `${rule.name} is now ${newStatus.toLowerCase()}`);
  };

  const handleDeleteRule = () => {
    if (!ruleToDelete) return;
    setRules(prev => prev.filter(r => r.id !== ruleToDelete.id));
    addToast('success', 'Rule Deleted', `${ruleToDelete.name} has been deleted`);
    setShowDeleteConfirm(false);
    setRuleToDelete(null);
  };

  const handleDuplicateRule = (rule: CommissionRule) => {
    const newRule: CommissionRule = {
      ...rule,
      id: `r${Date.now()}`,
      name: `${rule.name} (Copy)`,
      status: 'DRAFT',
      appliedCount: 0,
      totalEarnings: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setRules(prev => [...prev, newRule]);
    addToast('success', 'Rule Duplicated', `${rule.name} has been copied`);
  };

  // ─── Builder Handlers ────────────────────────────────────
  const addCondition = () => {
    setBuilderRule(prev => ({
      ...prev,
      conditions: [...prev.conditions, { id: `c${Date.now()}`, field: 'role', operator: 'equals', value: 'AGENT' }],
    }));
  };

  const removeCondition = (id: string) => {
    setBuilderRule(prev => ({ ...prev, conditions: prev.conditions.filter(c => c.id !== id) }));
  };

  const updateCondition = (id: string, updates: Partial<RuleCondition>) => {
    setBuilderRule(prev => ({
      ...prev,
      conditions: prev.conditions.map(c => c.id === id ? { ...c, ...updates } : c),
    }));
  };

  const addAction = () => {
    setBuilderRule(prev => ({
      ...prev,
      actions: [...prev.actions, { id: `a${Date.now()}`, type: 'PERCENTAGE', value: 5, target: 'saleAmount' }],
    }));
  };

  const removeAction = (id: string) => {
    setBuilderRule(prev => ({ ...prev, actions: prev.actions.filter(a => a.id !== id) }));
  };

  const updateAction = (id: string, updates: Partial<RuleAction>) => {
    setBuilderRule(prev => ({
      ...prev,
      actions: prev.actions.map(a => a.id === id ? { ...a, ...updates } : a),
    }));
  };

  const handleSaveBuilderRule = () => {
    if (!builderRule.name) {
      addToast('error', 'Validation', 'Rule name is required');
      return;
    }
    if (builderRule.actions.length === 0) {
      addToast('error', 'Validation', 'At least one action is required');
      return;
    }
    const newRule: CommissionRule = {
      ...builderRule,
      id: `r${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setRules(prev => [...prev, newRule]);
    setBuilderRule({
      id: '', name: '', description: '', type: 'PERCENTAGE', status: 'DRAFT',
      priority: 1, conditions: [], actions: [], createdBy: 'Current User',
      createdAt: '', updatedAt: '', appliedCount: 0, totalEarnings: 0,
    });
    setViewMode('list');
    addToast('success', 'Rule Created', `${newRule.name} has been saved`);
  };

  const handleEditInBuilder = (rule: CommissionRule) => {
    setBuilderRule(rule);
    setShowRuleModal(false);
    setSelectedRule(null);
    setViewMode('builder');
  };

  const simulateCommission = () => {
    const amount = parseFloat(simSaleAmount) || 0;
    let total = 0;
    for (const action of builderRule.actions) {
      if (action.type === 'PERCENTAGE') total += amount * (action.value / 100);
      else if (action.type === 'FIXED') total += action.value;
      else if (action.type === 'MULTIPLIER') total += amount * action.value;
    }
    return total;
  };

  const handleSaveSettings = () => {
    addToast('success', 'Settings Saved', 'Commission settings have been updated');
  };

  const handleCreateFromTemplate = (template: RuleTemplate) => {
    const newRule: CommissionRule = {
      id: `r${Date.now()}`,
      name: template.name,
      description: template.description,
      type: template.config.type || 'PERCENTAGE',
      status: 'DRAFT',
      priority: rules.length + 1,
      conditions: template.config.conditions || [],
      actions: template.config.actions || [],
      createdBy: 'Current User',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      appliedCount: 0,
      totalEarnings: 0,
    };
    setRules(prev => [...prev, newRule]);
    setViewMode('list');
    addToast('success', 'Rule Created', `${template.name} rule has been created from template`);
  };

  // ─── Render Views ─────────────────────────────────────────

  const renderToolbar = () => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
      <div style={{ display: 'flex', gap: '10px' }}>
        {(['list', 'builder', 'templates', 'analytics', 'settings'] as ViewMode[]).map(mode => {
          const icons = {
            list: 'solar:list-bold-duotone',
            builder: 'solar:widget-2-bold-duotone',
            templates: 'solar:document-add-bold-duotone',
            analytics: 'solar:chart-2-bold-duotone',
            settings: 'solar:settings-bold-duotone',
          };
          const labels = {
            list: 'Rules',
            builder: 'Builder',
            templates: 'Templates',
            analytics: 'Analytics',
            settings: 'Settings',
          };
          return (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 14px', borderRadius: 'var(--radius-md)',
                background: viewMode === mode ? 'var(--primary-600)' : 'rgba(148,163,184,0.08)',
                border: viewMode === mode ? '1px solid var(--primary-500)' : '1px solid transparent',
                color: viewMode === mode ? '#fff' : 'var(--text-secondary)',
                cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                transition: 'all 0.15s',
              }}
            >
              <Icon icon={icons[mode]} width={16} />
              {labels[mode]}
            </button>
          );
        })}
      </div>
      {viewMode === 'list' && (
        <Button variant="primary" icon="solar:add-circle-bold-duotone" onClick={() => setViewMode('templates')}>
          New Rule
        </Button>
      )}
    </div>
  );

  const renderRulesList = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Search & Filters */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Icon icon="solar:magnifer-bold-duotone" width={18} style={{
            position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
            color: 'var(--text-muted)',
          }} />
          <input
            placeholder="Search rules by name or description..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px 10px 38px', borderRadius: 'var(--radius-md)',
              background: 'var(--input-bg)', border: '1px solid var(--input-border)',
              color: 'var(--input-text)', fontSize: '13px', outline: 'none',
            }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as any)}
          style={{
            padding: '10px 32px 10px 12px', borderRadius: 'var(--radius-md)',
            background: 'var(--input-bg)', border: '1px solid var(--input-border)',
            color: 'var(--input-text)', fontSize: '13px', cursor: 'pointer',
          }}
        >
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="DRAFT">Draft</option>
          <option value="ARCHIVED">Archived</option>
        </select>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value as any)}
          style={{
            padding: '10px 32px 10px 12px', borderRadius: 'var(--radius-md)',
            background: 'var(--input-bg)', border: '1px solid var(--input-border)',
            color: 'var(--input-text)', fontSize: '13px', cursor: 'pointer',
          }}
        >
          <option value="ALL">All Types</option>
          <option value="PERCENTAGE">Percentage</option>
          <option value="FIXED">Fixed Amount</option>
          <option value="TIERED">Tiered</option>
          <option value="TEAM_SPLIT">Team Split</option>
          <option value="OVERRIDE">Override</option>
        </select>
      </div>

      {/* Rules Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '16px' }}>
        {filteredRules.map(rule => {
          const typeConfig = RULE_TYPE_CONFIG[rule.type];
          const statusConfig = STATUS_CONFIG[rule.status];
          return (
            <GlassCard key={rule.id} style={{ padding: '18px', cursor: 'pointer' }}
              onClick={() => { setSelectedRule(rule); setShowRuleModal(true); }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: 'var(--radius-md)',
                    background: `${typeConfig.color}20`, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Icon icon={typeConfig.icon} width={20} style={{ color: typeConfig.color }} />
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>{rule.name}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px' }}>
                      Priority {rule.priority}
                    </div>
                  </div>
                </div>
                <Badge variant={statusConfig.variant}>
                  <Icon icon={statusConfig.icon} width={12} style={{ marginRight: '4px' }} />
                  {statusConfig.label}
                </Badge>
              </div>

              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '14px', lineHeight: 1.5 }}>
                {rule.description}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                <div style={{
                  padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                  background: 'rgba(148,163,184,0.05)',
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-faint)', marginBottom: '2px' }}>Applied</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: 600 }}>
                    {rule.appliedCount} times
                  </div>
                </div>
                <div style={{
                  padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                  background: 'rgba(13,148,136,0.08)',
                }}>
                  <div style={{ fontSize: '10px', color: 'var(--text-faint)', marginBottom: '2px' }}>Total Earned</div>
                  <div style={{ fontSize: '14px', color: 'var(--primary-400)', fontWeight: 600 }}>
                    {formatCurrency(rule.totalEarnings)}
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex', gap: '6px', paddingTop: '12px',
                borderTop: '1px solid rgba(148,163,184,0.08)',
              }}
                onClick={e => e.stopPropagation()}>
                <button
                  onClick={() => handleToggleStatus(rule)}
                  style={{
                    flex: 1, padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                    background: 'rgba(148,163,184,0.08)', border: 'none',
                    color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '4px',
                  }}>
                  <Icon icon={rule.status === 'ACTIVE' ? 'solar:pause-bold-duotone' : 'solar:play-bold-duotone'} width={12} />
                  {rule.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDuplicateRule(rule)}
                  style={{
                    flex: 1, padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                    background: 'rgba(148,163,184,0.08)', border: 'none',
                    color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: '4px',
                  }}>
                  <Icon icon="solar:copy-bold-duotone" width={12} />
                  Duplicate
                </button>
                <button
                  onClick={() => { setRuleToDelete(rule); setShowDeleteConfirm(true); }}
                  style={{
                    padding: '6px 10px', borderRadius: 'var(--radius-sm)',
                    background: 'rgba(239,68,68,0.10)', border: 'none',
                    color: '#EF4444', fontSize: '11px', fontWeight: 600,
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Icon icon="solar:trash-bin-trash-bold-duotone" width={12} />
                </button>
              </div>
            </GlassCard>
          );
        })}
      </div>

      {filteredRules.length === 0 && (
        <div style={{
          padding: '60px 20px', textAlign: 'center',
          border: '2px dashed rgba(148,163,184,0.15)', borderRadius: 'var(--radius-lg)',
        }}>
          <Icon icon="solar:document-bold-duotone" width={48} style={{ color: 'var(--text-faint)', marginBottom: '12px' }} />
          <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '8px' }}>No rules found</div>
          <div style={{ color: 'var(--text-faint)', fontSize: '12px' }}>Try adjusting your search or filters</div>
        </div>
      )}
    </div>
  );

  const renderTemplates = () => (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <SectionHeader
          title="Rule Templates"
          icon="solar:document-add-bold-duotone"
        />
        <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '6px' }}>
          Start with a pre-built template and customize to your needs
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginTop: '20px' }}>
        {RULE_TEMPLATES.map(template => (
          <GlassCard key={template.id} style={{ padding: '20px', cursor: 'pointer' }}
            onClick={() => handleCreateFromTemplate(template)}>
            <div style={{
              width: '48px', height: '48px', borderRadius: 'var(--radius-lg)',
              background: `${template.color}20`, display: 'flex',
              alignItems: 'center', justifyContent: 'center', marginBottom: '14px',
            }}>
              <Icon icon={template.icon} width={24} style={{ color: template.color }} />
            </div>
            <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '15px', marginBottom: '6px' }}>
              {template.name}
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.5, marginBottom: '12px' }}>
              {template.description}
            </div>
            <Badge variant="info" style={{ fontSize: '10px' }}>{template.category}</Badge>
          </GlassCard>
        ))}
      </div>
    </div>
  );

  // ─── Sandbox State ────────────────────────────────────────
  const [sandboxForm, setSandboxForm] = useState({ saleAmount: '200000', paymentType: 'INSTALLMENT', agentRole: 'AGENT', sectionId: '' });
  const [sandboxResult, setSandboxResult] = useState<any>(null);
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const [agentReport, setAgentReport] = useState<any>(null);
  const [agentReportLoading, setAgentReportLoading] = useState(false);

  const handleSandboxTest = async () => {
    setSandboxLoading(true);
    try {
      const result = await api.commissionRules.test({
        saleAmount: Number(sandboxForm.saleAmount),
        paymentType: sandboxForm.paymentType,
        agentRole: sandboxForm.agentRole,
        sectionId: sandboxForm.sectionId || undefined,
      });
      setSandboxResult(result);
    } catch (e: any) {
      addToast('error', 'Test Failed', e.message);
    }
    setSandboxLoading(false);
  };

  const fetchAgentReport = async () => {
    setAgentReportLoading(true);
    try {
      const data = await api.commissionRules.agentSummary();
      setAgentReport(data);
    } catch { /* keep null */ }
    setAgentReportLoading(false);
  };

  const renderAnalytics = () => (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <SectionHeader
          title="Commission Analytics"
          icon="solar:chart-2-bold-duotone"
        />
        <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginTop: '6px' }}>
          Test sandbox, agent earnings, and rule performance
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginTop: '20px' }}>
        {[
          { label: 'Total Rules', value: rules.length, icon: 'solar:document-bold-duotone', color: '#0D9488' },
          { label: 'Active Rules', value: rules.filter(r => r.status === 'ACTIVE').length, icon: 'solar:check-circle-bold-duotone', color: '#22C55E' },
          { label: 'Total Applied', value: rules.reduce((sum, r) => sum + r.appliedCount, 0), icon: 'solar:chart-bold-duotone', color: '#3B82F6' },
          { label: 'Total Earnings', value: formatCurrency(rules.reduce((sum, r) => sum + r.totalEarnings, 0)), icon: 'solar:wallet-money-bold-duotone', color: '#F59E0B' },
        ].map((stat, i) => (
          <GlassCard key={i} style={{ padding: '18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: 'var(--radius-md)',
                background: `${stat.color}20`, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon icon={stat.icon} width={22} style={{ color: stat.color }} />
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '2px' }}>{stat.label}</div>
                <div style={{ fontSize: '20px', color: 'var(--text-primary)', fontWeight: 700 }}>{stat.value}</div>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '20px' }}>
        {/* ── Commission Test Sandbox ───────────────── */}
        <GlassCard style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Icon icon="solar:test-tube-bold-duotone" width={20} style={{ color: 'var(--primary-400)' }} />
            <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '15px' }}>Commission Sandbox</div>
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginBottom: '14px' }}>
            Test commission calculation with different parameters
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Sale Amount</label>
              <input type="number" value={sandboxForm.saleAmount} onChange={e => setSandboxForm(f => ({ ...f, saleAmount: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }} />
            </div>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Payment Type</label>
              <select value={sandboxForm.paymentType} onChange={e => setSandboxForm(f => ({ ...f, paymentType: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', cursor: 'pointer' }}>
                <option value="INSTALLMENT">Installment</option>
                <option value="CASH">Cash</option>
                <option value="SPOT_CASH">Spot Cash</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Agent Role</label>
              <select value={sandboxForm.agentRole} onChange={e => setSandboxForm(f => ({ ...f, agentRole: e.target.value }))}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', cursor: 'pointer' }}>
                <option value="AGENT">Agent</option>
                <option value="MANAGER">Manager</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button variant="primary" size="sm" fullWidth icon="solar:play-bold-duotone" onClick={handleSandboxTest}>
                {sandboxLoading ? 'Testing...' : 'Calculate'}
              </Button>
            </div>
          </div>

          {/* Result */}
          {sandboxResult && (
            <div style={{
              marginTop: '8px', padding: '14px', borderRadius: 'var(--radius-md)',
              background: 'rgba(13,148,136,0.06)', border: '1px solid rgba(13,148,136,0.15)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Total Commission</span>
                <span style={{ fontSize: '22px', fontWeight: 800, color: 'var(--primary-400)', fontFamily: 'var(--font-heading)' }}>
                  {formatCurrency(sandboxResult.total)}
                </span>
              </div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '6px' }}>
                {sandboxResult.rulesApplied} rule(s) applied · {sandboxResult.rulesEvaluated} evaluated
              </div>
              {sandboxResult.breakdown && sandboxResult.breakdown.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {sandboxResult.breakdown.map((b: any, i: number) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', padding: '6px 8px',
                      borderRadius: '4px', background: 'var(--bg-elevated)',
                    }}>
                      <div>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: 600 }}>{b.ruleName}</span>
                        <span style={{ fontSize: '10px', color: 'var(--text-faint)', marginLeft: '6px' }}>{b.description}</span>
                      </div>
                      <span style={{ fontSize: '12px', color: 'var(--primary-400)', fontWeight: 700 }}>
                        {formatCurrency(b.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {(!sandboxResult.breakdown || sandboxResult.breakdown.length === 0) && sandboxResult.total === 0 && (
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>
                  No rules matched these parameters
                </div>
              )}
            </div>
          )}
        </GlassCard>

        {/* ── Agent Commission Report ───────────────── */}
        <GlassCard style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon="solar:users-group-rounded-bold-duotone" width={20} style={{ color: '#3B82F6' }} />
              <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '15px' }}>Agent Earnings</div>
            </div>
            <Button variant="ghost" size="sm" icon="solar:refresh-bold-duotone" onClick={fetchAgentReport}>
              {agentReportLoading ? 'Loading...' : 'Load'}
            </Button>
          </div>

          {agentReport ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                {[
                  { label: 'Sales', value: agentReport.totals.totalSales, color: '#3B82F6' },
                  { label: 'Revenue', value: formatCurrency(agentReport.totals.totalRevenue), color: '#0D9488' },
                  { label: 'Commission', value: formatCurrency(agentReport.totals.totalCommission), color: '#F59E0B' },
                ].map((s, i) => (
                  <div key={i} style={{ padding: '8px', borderRadius: '6px', background: 'rgba(148,163,184,0.05)', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: 'var(--text-faint)' }}>{s.label}</div>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {agentReport.summary.map((agent: any) => (
                  <div key={agent.agent.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 12px', borderRadius: 'var(--radius-md)',
                    background: 'rgba(148,163,184,0.05)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(59,130,246,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon icon="solar:user-bold-duotone" width={16} style={{ color: '#3B82F6' }} />
                      </div>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{agent.agent.name}</div>
                        <div style={{ color: 'var(--text-faint)', fontSize: '10px' }}>
                          {agent.totalSales} sales · {formatCurrency(agent.totalRevenue)} revenue
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: 'var(--primary-400)', fontSize: '14px', fontWeight: 700 }}>
                        {formatCurrency(agent.totalCommission)}
                      </div>
                      <div style={{ fontSize: '10px', color: 'var(--text-faint)' }}>
                        {agent.totalRevenue > 0 ? ((agent.totalCommission / agent.totalRevenue) * 100).toFixed(1) : '0'}% avg
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ color: 'var(--text-muted)', fontSize: '12px', textAlign: 'center', padding: '30px 0' }}>
              Click "Load" to fetch agent commission report from live data
            </div>
          )}
        </GlassCard>
      </div>

      {/* Top performing rules */}
      <GlassCard style={{ padding: '24px', marginTop: '16px' }}>
        <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '15px', marginBottom: '16px' }}>Top Performing Rules</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {rules
            .sort((a, b) => b.totalEarnings - a.totalEarnings)
            .slice(0, 5)
            .map(rule => (
              <div key={rule.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px', borderRadius: 'var(--radius-md)',
                background: 'rgba(148,163,184,0.05)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Icon icon={RULE_TYPE_CONFIG[rule.type].icon} width={18}
                    style={{ color: RULE_TYPE_CONFIG[rule.type].color }} />
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{rule.name}</div>
                    <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{rule.appliedCount} applications</div>
                  </div>
                </div>
                <div style={{ color: 'var(--primary-400)', fontSize: '14px', fontWeight: 700 }}>
                  {formatCurrency(rule.totalEarnings)}
                </div>
              </div>
            ))}
        </div>
      </GlassCard>
    </div>
  );

  return (
    <>
      <TopBar title="Commission Rules" />
      <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }}>
        {renderToolbar()}
        
        {viewMode === 'list' && renderRulesList()}
        {viewMode === 'templates' && renderTemplates()}
        {viewMode === 'analytics' && renderAnalytics()}
        {viewMode === 'builder' && (
          <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr 280px', gap: '16px', minHeight: '600px' }}>
            {/* Left - Toolbox */}
            <GlassCard style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>
                <Icon icon="solar:widget-2-bold-duotone" width={16} style={{ marginRight: '6px', color: 'var(--primary-500)' }} />
                Toolbox
              </div>

              <div style={{ fontSize: '10px', color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Conditions</div>
              {[
                { field: 'role', label: 'Agent Role', icon: 'solar:user-bold-duotone', color: '#3B82F6' },
                { field: 'saleAmount', label: 'Sale Amount', icon: 'solar:tag-price-bold-duotone', color: '#F59E0B' },
                { field: 'saleStatus', label: 'Sale Status', icon: 'solar:check-circle-bold-duotone', color: '#22C55E' },
                { field: 'productCategory', label: 'Product Type', icon: 'solar:box-bold-duotone', color: '#8B5CF6' },
                { field: 'hasReferrer', label: 'Has Referrer', icon: 'solar:users-group-rounded-bold-duotone', color: '#EC4899' },
                { field: 'saleDate', label: 'Sale Date', icon: 'solar:calendar-bold-duotone', color: '#06B6D4' },
              ].map(item => (
                <button key={item.field} onClick={() => {
                  setBuilderRule(prev => ({
                    ...prev,
                    conditions: [...prev.conditions, { id: `c${Date.now()}`, field: item.field, operator: 'equals', value: '' }],
                  }));
                }} style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)', background: 'rgba(148,163,184,0.06)',
                  border: '1px solid rgba(148,163,184,0.10)', cursor: 'pointer',
                  color: 'var(--text-secondary)', fontSize: '12px', fontWeight: 500,
                  transition: 'all 0.15s',
                }}>
                  <Icon icon={item.icon} width={14} style={{ color: item.color }} />
                  {item.label}
                  <Icon icon="solar:add-circle-bold-duotone" width={12} style={{ marginLeft: 'auto', color: 'var(--text-faint)' }} />
                </button>
              ))}

              <div style={{ fontSize: '10px', color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '8px' }}>Actions</div>
              {[
                { type: 'PERCENTAGE' as const, label: '% Commission', icon: 'solar:percent-bold-duotone', color: '#0D9488' },
                { type: 'FIXED' as const, label: 'Fixed Amount', icon: 'solar:wallet-money-bold-duotone', color: '#F59E0B' },
                { type: 'MULTIPLIER' as const, label: 'Multiplier', icon: 'solar:calculator-bold-duotone', color: '#3B82F6' },
              ].map(item => (
                <button key={item.type} onClick={() => {
                  setBuilderRule(prev => ({
                    ...prev,
                    actions: [...prev.actions, { id: `a${Date.now()}`, type: item.type, value: item.type === 'PERCENTAGE' ? 5 : item.type === 'FIXED' ? 5000 : 1.5, target: 'saleAmount' }],
                  }));
                }} style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)', background: `${item.color}10`,
                  border: `1px solid ${item.color}30`, cursor: 'pointer',
                  color: item.color, fontSize: '12px', fontWeight: 600,
                  transition: 'all 0.15s',
                }}>
                  <Icon icon={item.icon} width={14} />
                  {item.label}
                  <Icon icon="solar:add-circle-bold-duotone" width={12} style={{ marginLeft: 'auto' }} />
                </button>
              ))}
            </GlassCard>

            {/* Center - Canvas */}
            <GlassCard style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>
              {/* Rule Info */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>Rule Name</label>
                  <input value={builderRule.name} onChange={e => setBuilderRule(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Standard Agent Commission"
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>Type</label>
                    <select value={builderRule.type} onChange={e => setBuilderRule(prev => ({ ...prev, type: e.target.value as RuleType }))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', cursor: 'pointer' }}>
                      {Object.entries(RULE_TYPE_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>Priority</label>
                    <input type="number" value={builderRule.priority} onChange={e => setBuilderRule(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }} />
                  </div>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>Description</label>
                <input value={builderRule.description} onChange={e => setBuilderRule(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What does this rule do?"
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }} />
              </div>

              {/* Visual Flow */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0' }}>
                {/* Start Node */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div style={{ padding: '8px 20px', borderRadius: 'var(--radius-full)', background: 'rgba(13,148,136,0.15)', border: '1px solid rgba(13,148,136,0.30)', color: 'var(--primary-400)', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Icon icon="solar:play-bold-duotone" width={14} /> Sale Event Triggered
                  </div>
                </div>
                <div style={{ width: '2px', height: '20px', background: 'rgba(148,163,184,0.15)', margin: '0 auto' }} />

                {/* Conditions */}
                {builderRule.conditions.length > 0 && (
                  <>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', marginBottom: '8px' }}>
                      <Icon icon="solar:filter-bold-duotone" width={12} style={{ marginRight: '4px' }} />
                      Conditions (IF)
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '4px' }}>
                      {builderRule.conditions.map((cond, idx) => (
                        <div key={cond.id} style={{
                          display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
                          borderRadius: 'var(--radius-md)', background: 'rgba(59,130,246,0.06)',
                          border: '1px solid rgba(59,130,246,0.15)',
                        }}>
                          {idx > 0 && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>AND</span>}
                          <select value={cond.field} onChange={e => updateCondition(cond.id, { field: e.target.value })}
                            style={{ padding: '5px 8px', borderRadius: '4px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '12px', cursor: 'pointer' }}>
                            <option value="role">Role</option>
                            <option value="saleAmount">Sale Amount</option>
                            <option value="saleStatus">Sale Status</option>
                            <option value="productCategory">Product Type</option>
                            <option value="hasReferrer">Has Referrer</option>
                            <option value="saleDate">Sale Date</option>
                          </select>
                          <select value={cond.operator} onChange={e => updateCondition(cond.id, { operator: e.target.value })}
                            style={{ padding: '5px 8px', borderRadius: '4px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '12px', cursor: 'pointer' }}>
                            <option value="equals">equals</option>
                            <option value="notEquals">not equals</option>
                            <option value="greaterThan">greater than</option>
                            <option value="lessThan">less than</option>
                            <option value="between">between</option>
                            <option value="contains">contains</option>
                          </select>
                          <input value={typeof cond.value === 'string' ? cond.value : JSON.stringify(cond.value)}
                            onChange={e => updateCondition(cond.id, { value: e.target.value })}
                            placeholder="Value"
                            style={{ flex: 1, padding: '5px 8px', borderRadius: '4px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '12px', outline: 'none' }} />
                          <button onClick={() => removeCondition(cond.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '2px' }}>
                            <Icon icon="solar:close-circle-bold-duotone" width={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ width: '2px', height: '20px', background: 'rgba(148,163,184,0.15)', margin: '0 auto' }} />
                  </>
                )}

                {/* Actions */}
                {builderRule.actions.length > 0 && (
                  <>
                    <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center', marginBottom: '8px' }}>
                      <Icon icon="solar:bolt-bold-duotone" width={12} style={{ marginRight: '4px' }} />
                      Actions (THEN)
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '4px' }}>
                      {builderRule.actions.map(action => (
                        <div key={action.id} style={{
                          display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px',
                          borderRadius: 'var(--radius-md)', background: 'rgba(13,148,136,0.06)',
                          border: '1px solid rgba(13,148,136,0.15)',
                        }}>
                          <select value={action.type} onChange={e => updateAction(action.id, { type: e.target.value as any })}
                            style={{ padding: '5px 8px', borderRadius: '4px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--primary-400)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                            <option value="PERCENTAGE">% of</option>
                            <option value="FIXED">Fixed ₱</option>
                            <option value="MULTIPLIER">Multiply by</option>
                          </select>
                          <input type="number" value={action.value}
                            onChange={e => updateAction(action.id, { value: parseFloat(e.target.value) || 0 })}
                            style={{ width: '80px', padding: '5px 8px', borderRadius: '4px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '12px', outline: 'none', textAlign: 'center' }} />
                          <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>on</span>
                          <select value={action.target} onChange={e => updateAction(action.id, { target: e.target.value })}
                            style={{ padding: '5px 8px', borderRadius: '4px', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '12px', cursor: 'pointer' }}>
                            <option value="saleAmount">Sale Amount</option>
                            <option value="netAmount">Net Amount</option>
                            <option value="downPayment">Down Payment</option>
                          </select>
                          <button onClick={() => removeAction(action.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: '2px' }}>
                            <Icon icon="solar:close-circle-bold-duotone" width={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ width: '2px', height: '20px', background: 'rgba(148,163,184,0.15)', margin: '0 auto' }} />
                  </>
                )}

                {/* End Node */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <div style={{ padding: '8px 20px', borderRadius: 'var(--radius-full)', background: 'rgba(148,163,184,0.08)', border: '1px solid rgba(148,163,184,0.15)', color: 'var(--text-muted)', fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Icon icon="solar:flag-bold-duotone" width={14} /> Commission Calculated
                  </div>
                </div>

                {/* Empty State */}
                {builderRule.conditions.length === 0 && builderRule.actions.length === 0 && (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '8px', padding: '40px', opacity: 0.6 }}>
                    <Icon icon="solar:arrow-left-bold-duotone" width={24} style={{ color: 'var(--text-faint)' }} />
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center' }}>
                      Click items in the toolbox to add conditions and actions
                    </div>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '12px', borderTop: '1px solid rgba(148,163,184,0.08)' }}>
                <Button variant="secondary" icon="solar:test-tube-bold-duotone" onClick={() => setShowSimulator(true)}>
                  Simulate
                </Button>
                <Button variant="primary" icon="solar:diskette-bold-duotone" onClick={handleSaveBuilderRule}>
                  Save Rule
                </Button>
              </div>
            </GlassCard>

            {/* Right - Preview & Simulator */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Live Preview */}
              <GlassCard style={{ padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon icon="solar:eye-bold-duotone" width={14} style={{ color: 'var(--primary-500)' }} />
                  Live Preview
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Name</span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '11px', fontWeight: 500 }}>{builderRule.name || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Type</span>
                    <span style={{ color: RULE_TYPE_CONFIG[builderRule.type].color, fontSize: '11px', fontWeight: 600 }}>
                      {RULE_TYPE_CONFIG[builderRule.type].label}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Conditions</span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '11px' }}>{builderRule.conditions.length}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>Actions</span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '11px' }}>{builderRule.actions.length}</span>
                  </div>
                </div>
              </GlassCard>

              {/* Quick Simulator */}
              <GlassCard style={{ padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon icon="solar:calculator-bold-duotone" width={14} style={{ color: '#F59E0B' }} />
                  Quick Simulator
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Sale Amount (₱)</label>
                    <input type="number" value={simSaleAmount} onChange={e => setSimSaleAmount(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '12px', outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Agent Role</label>
                    <select value={simRole} onChange={e => setSimRole(e.target.value)}
                      style={{ width: '100%', padding: '7px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '12px', cursor: 'pointer' }}>
                      <option value="AGENT">Agent</option>
                      <option value="MANAGER">Manager</option>
                      <option value="BROKER">Broker</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ padding: '12px', borderRadius: 'var(--radius-md)', background: 'rgba(13,148,136,0.10)', marginTop: '4px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px' }}>Estimated Commission</div>
                      <div style={{ fontSize: '20px', color: 'var(--primary-400)', fontWeight: 700 }}>
                        {formatCurrency(simulateCommission())}
                      </div>
                      {builderRule.actions.map(a => (
                        <div key={a.id} style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {a.type === 'PERCENTAGE' ? `${a.value}% of ${formatCurrency(parseFloat(simSaleAmount) || 0)}` :
                           a.type === 'FIXED' ? `Fixed ${formatCurrency(a.value)}` :
                           `${a.value}x multiplier`}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </GlassCard>

              {/* JSON Preview */}
              <GlassCard style={{ padding: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon icon="solar:code-bold-duotone" width={14} style={{ color: '#8B5CF6' }} />
                  Rule JSON
                </div>
                <pre style={{
                  padding: '10px', borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-elevated)', border: '1px solid var(--border-base)',
                  color: 'var(--text-secondary)', fontSize: '10px', fontFamily: 'var(--font-code)',
                  overflowX: 'auto', maxHeight: '160px', overflowY: 'auto',
                  scrollbarWidth: 'none', margin: 0,
                }}>
                  {JSON.stringify({ type: builderRule.type, conditions: builderRule.conditions.map(c => ({ field: c.field, operator: c.operator, value: c.value })), actions: builderRule.actions.map(a => ({ type: a.type, value: a.value, target: a.target })) }, null, 2)}
                </pre>
              </GlassCard>
            </div>
          </div>
        )}
        {viewMode === 'settings' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Rate Configuration */}
            <GlassCard style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Icon icon="solar:percent-bold-duotone" width={18} style={{ color: 'var(--primary-500)' }} />
                <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>Rate Configuration</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[
                  { label: 'Default Commission Rate (%)', key: 'defaultRate', type: 'number' },
                  { label: 'Minimum Rate (%)', key: 'minRate', type: 'number' },
                  { label: 'Maximum Rate (%)', key: 'maxRate', type: 'number' },
                  { label: 'Tax Deduction (%)', key: 'taxDeduction', type: 'number' },
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>{field.label}</label>
                    <input type="number" value={(settings as any)[field.key]}
                      onChange={e => setSettings(prev => ({ ...prev, [field.key]: parseFloat(e.target.value) || 0 }))}
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', outline: 'none' }} />
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Payout Settings */}
            <GlassCard style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Icon icon="solar:wallet-money-bold-duotone" width={18} style={{ color: '#F59E0B' }} />
                <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '14px' }}>Payout Settings</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Payout Schedule</label>
                  <select value={settings.payoutSchedule}
                    onChange={e => setSettings(prev => ({ ...prev, payoutSchedule: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', cursor: 'pointer' }}>
                    <option value="WEEKLY">Weekly</option>
                    <option value="BIWEEKLY">Bi-weekly</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="ON_COMPLETION">On Sale Completion</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Currency</label>
                  <select value={settings.currency}
                    onChange={e => setSettings(prev => ({ ...prev, currency: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', cursor: 'pointer' }}>
                    <option value="PHP">Philippine Peso (₱)</option>
                    <option value="USD">US Dollar ($)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Rounding Mode</label>
                  <select value={settings.roundingMode}
                    onChange={e => setSettings(prev => ({ ...prev, roundingMode: e.target.value }))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'var(--input-bg)', border: '1px solid var(--input-border)', color: 'var(--input-text)', fontSize: '13px', cursor: 'pointer' }}>
                    <option value="NEAREST">Round to Nearest</option>
                    <option value="UP">Round Up</option>
                    <option value="DOWN">Round Down</option>
                    <option value="NONE">No Rounding</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: 'var(--dark-400)', display: 'block', marginBottom: '6px' }}>Auto-Approve Below (₱)</label>
                  <input type="number" value={settings.autoApproveBelow}
                    onChange={e => setSettings(prev => ({ ...prev, autoApproveBelow: parseFloat(e.target.value) || 0 }))}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)', background: 'rgba(15,23,42,0.40)', border: '1px solid var(--glass-border)', color: 'var(--dark-200)', fontSize: '13px', outline: 'none' }} />
                </div>
              </div>
            </GlassCard>

            {/* Approval Workflow */}
            <GlassCard style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Icon icon="solar:shield-check-bold-duotone" width={18} style={{ color: '#3B82F6' }} />
                <span style={{ color: 'var(--dark-100)', fontWeight: 600, fontSize: '14px' }}>Approval Workflow</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { key: 'requireManagerApproval', label: 'Require Manager Approval', desc: 'Commissions require manager sign-off before payout', icon: 'solar:user-check-bold-duotone' },
                  { key: 'requireBrokerApproval', label: 'Require Broker Approval', desc: 'Large commissions require broker-level approval', icon: 'solar:shield-user-bold-duotone' },
                ].map(item => (
                  <div key={item.key} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px', borderRadius: 'var(--radius-md)', background: 'rgba(148,163,184,0.05)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Icon icon={item.icon} width={16} style={{ color: 'var(--dark-400)' }} />
                      <div>
                        <div style={{ color: 'var(--dark-200)', fontSize: '13px', fontWeight: 500 }}>{item.label}</div>
                        <div style={{ color: 'var(--dark-600)', fontSize: '11px' }}>{item.desc}</div>
                      </div>
                    </div>
                    <button onClick={() => setSettings(prev => ({ ...prev, [item.key]: !(prev as any)[item.key] }))}
                      style={{
                        width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
                        background: (settings as any)[item.key] ? 'var(--primary-600)' : 'rgba(148,163,184,0.20)',
                        position: 'relative', transition: 'background 0.2s',
                      }}>
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '3px',
                        left: (settings as any)[item.key] ? '21px' : '3px',
                        transition: 'left 0.2s',
                      }} />
                    </button>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Feature Toggles */}
            <GlassCard style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Icon icon="solar:tuning-2-bold-duotone" width={18} style={{ color: '#8B5CF6' }} />
                <span style={{ color: 'var(--dark-100)', fontWeight: 600, fontSize: '14px' }}>Feature Toggles</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { key: 'enableOverrides', label: 'Commission Overrides', desc: 'Allow override rules to supersede standard commissions', icon: 'solar:shield-warning-bold-duotone' },
                  { key: 'enableTeamSplit', label: 'Team Split', desc: 'Enable commission splitting between team members', icon: 'solar:users-group-rounded-bold-duotone' },
                  { key: 'enableTiered', label: 'Tiered Commissions', desc: 'Allow tiered rates based on sale value brackets', icon: 'solar:layers-bold-duotone' },
                ].map(item => (
                  <div key={item.key} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px', borderRadius: 'var(--radius-md)', background: 'rgba(148,163,184,0.05)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Icon icon={item.icon} width={16} style={{ color: 'var(--dark-400)' }} />
                      <div>
                        <div style={{ color: 'var(--dark-200)', fontSize: '13px', fontWeight: 500 }}>{item.label}</div>
                        <div style={{ color: 'var(--dark-600)', fontSize: '11px' }}>{item.desc}</div>
                      </div>
                    </div>
                    <button onClick={() => setSettings(prev => ({ ...prev, [item.key]: !(prev as any)[item.key] }))}
                      style={{
                        width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
                        background: (settings as any)[item.key] ? 'var(--primary-600)' : 'rgba(148,163,184,0.20)',
                        position: 'relative', transition: 'background 0.2s',
                      }}>
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '3px',
                        left: (settings as any)[item.key] ? '21px' : '3px',
                        transition: 'left 0.2s',
                      }} />
                    </button>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Notifications */}
            <GlassCard style={{ padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Icon icon="solar:bell-bold-duotone" width={18} style={{ color: '#EC4899' }} />
                <span style={{ color: 'var(--dark-100)', fontWeight: 600, fontSize: '14px' }}>Notifications</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {[
                  { key: 'notifyOnEarning', label: 'Earning Notifications', desc: 'Notify agents when commission is earned' },
                  { key: 'notifyOnPayout', label: 'Payout Notifications', desc: 'Notify when commission payout is processed' },
                  { key: 'notifyOnRuleChange', label: 'Rule Change Alerts', desc: 'Notify admins when rules are modified' },
                ].map(item => (
                  <div key={item.key} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '12px', borderRadius: 'var(--radius-md)', background: 'rgba(148,163,184,0.05)',
                  }}>
                    <div>
                      <div style={{ color: 'var(--dark-200)', fontSize: '13px', fontWeight: 500 }}>{item.label}</div>
                      <div style={{ color: 'var(--dark-600)', fontSize: '11px' }}>{item.desc}</div>
                    </div>
                    <button onClick={() => setSettings(prev => ({ ...prev, [item.key]: !(prev as any)[item.key] }))}
                      style={{
                        width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
                        background: (settings as any)[item.key] ? 'var(--primary-600)' : 'rgba(148,163,184,0.20)',
                        position: 'relative', transition: 'background 0.2s',
                      }}>
                      <div style={{
                        width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: '3px',
                        left: (settings as any)[item.key] ? '21px' : '3px',
                        transition: 'left 0.2s',
                      }} />
                    </button>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Danger Zone */}
            <GlassCard style={{ padding: '20px', borderColor: 'rgba(239,68,68,0.20)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Icon icon="solar:danger-triangle-bold-duotone" width={18} style={{ color: '#EF4444' }} />
                <span style={{ color: '#EF4444', fontWeight: 600, fontSize: '14px' }}>Danger Zone</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px', borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.05)',
                  border: '1px solid rgba(239,68,68,0.12)',
                }}>
                  <div>
                    <div style={{ color: 'var(--dark-200)', fontSize: '13px', fontWeight: 500 }}>Archive All Draft Rules</div>
                    <div style={{ color: 'var(--dark-600)', fontSize: '11px' }}>Move all draft rules to archived status</div>
                  </div>
                  <button onClick={() => {
                    setRules(prev => prev.map(r => r.status === 'DRAFT' ? { ...r, status: 'ARCHIVED' as RuleStatus } : r));
                    addToast('info', 'Rules Archived', 'All draft rules have been archived');
                  }} style={{
                    padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                    background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)',
                    color: '#EF4444', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                  }}>Archive</button>
                </div>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px', borderRadius: 'var(--radius-md)', background: 'rgba(239,68,68,0.05)',
                  border: '1px solid rgba(239,68,68,0.12)',
                }}>
                  <div>
                    <div style={{ color: 'var(--dark-200)', fontSize: '13px', fontWeight: 500 }}>Reset All Rules</div>
                    <div style={{ color: 'var(--dark-600)', fontSize: '11px' }}>Delete all custom rules and restore defaults</div>
                  </div>
                  <button onClick={() => {
                    setRules(MOCK_RULES);
                    addToast('info', 'Rules Reset', 'All rules have been reset to defaults');
                  }} style={{
                    padding: '6px 14px', borderRadius: 'var(--radius-sm)',
                    background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.25)',
                    color: '#EF4444', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                  }}>Reset</button>
                </div>
              </div>
            </GlassCard>

            {/* Save Settings */}
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <Button variant="secondary" onClick={() => setViewMode('list')}>Cancel</Button>
              <Button variant="primary" icon="solar:diskette-bold-duotone" onClick={handleSaveSettings}>Save Settings</Button>
            </div>
          </div>
        )}
      </div>

      {/* Rule Detail Modal */}
      {selectedRule && (
        <Modal
          isOpen={showRuleModal}
          onClose={() => { setShowRuleModal(false); setSelectedRule(null); }}
          title={selectedRule.name}
          width="600px"
          footer={
            <>
              <Button variant="secondary" onClick={() => { setShowRuleModal(false); setSelectedRule(null); }}>Close</Button>
              <Button variant="primary" icon="solar:pen-bold-duotone" onClick={() => handleEditInBuilder(selectedRule)}>
                Edit in Builder
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Badge variant={STATUS_CONFIG[selectedRule.status].variant}>
                {STATUS_CONFIG[selectedRule.status].label}
              </Badge>
              <Badge variant="default">{RULE_TYPE_CONFIG[selectedRule.type].label}</Badge>
            </div>

            <div>
              <div style={{ fontSize: '11px', color: 'var(--dark-500)', marginBottom: '4px' }}>Description</div>
              <div style={{ color: 'var(--dark-200)', fontSize: '13px' }}>{selectedRule.description}</div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--dark-500)', marginBottom: '4px' }}>Priority</div>
                <div style={{ color: 'var(--dark-200)', fontSize: '13px' }}>{selectedRule.priority}</div>
              </div>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--dark-500)', marginBottom: '4px' }}>Created By</div>
                <div style={{ color: 'var(--dark-200)', fontSize: '13px' }}>{selectedRule.createdBy}</div>
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', color: 'var(--dark-500)', marginBottom: '8px' }}>Conditions ({selectedRule.conditions.length})</div>
              {selectedRule.conditions.map(cond => (
                <div key={cond.id} style={{
                  padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                  background: 'rgba(148,163,184,0.05)', marginBottom: '6px',
                  fontSize: '12px', color: 'var(--dark-300)',
                }}>
                  {cond.field} {cond.operator} {JSON.stringify(cond.value)}
                </div>
              ))}
            </div>

            <div>
              <div style={{ fontSize: '11px', color: 'var(--dark-500)', marginBottom: '8px' }}>Actions ({selectedRule.actions.length})</div>
              {selectedRule.actions.map(action => (
                <div key={action.id} style={{
                  padding: '8px 10px', borderRadius: 'var(--radius-sm)',
                  background: 'rgba(13,148,136,0.08)', marginBottom: '6px',
                  fontSize: '12px', color: 'var(--primary-400)', fontWeight: 600,
                }}>
                  {action.type}: {action.value}{action.type === 'PERCENTAGE' ? '%' : ''} on {action.target}
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={{ padding: '12px', borderRadius: 'var(--radius-md)', background: 'rgba(148,163,184,0.05)' }}>
                <div style={{ fontSize: '11px', color: 'var(--dark-500)', marginBottom: '4px' }}>Applied</div>
                <div style={{ fontSize: '18px', color: 'var(--dark-100)', fontWeight: 700 }}>{selectedRule.appliedCount}</div>
              </div>
              <div style={{ padding: '12px', borderRadius: 'var(--radius-md)', background: 'rgba(13,148,136,0.08)' }}>
                <div style={{ fontSize: '11px', color: 'var(--dark-500)', marginBottom: '4px' }}>Total Earned</div>
                <div style={{ fontSize: '18px', color: 'var(--primary-400)', fontWeight: 700 }}>
                  {formatCurrency(selectedRule.totalEarnings)}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setRuleToDelete(null); }}
        title="Delete Rule"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setShowDeleteConfirm(false); setRuleToDelete(null); }}>Cancel</Button>
            <Button variant="primary" icon="solar:trash-bin-trash-bold-duotone" onClick={handleDeleteRule}>
              Delete Rule
            </Button>
          </>
        }
      >
        {ruleToDelete && (
          <div style={{ color: 'var(--dark-300)', fontSize: '14px' }}>
            Are you sure you want to delete <strong style={{ color: 'var(--dark-100)' }}>{ruleToDelete.name}</strong>?
            This action cannot be undone.
          </div>
        )}
      </Modal>
    </>
  );
};

export default CommissionRules;
